import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const UserContextSwitcher = () => {
  const [currentRole, setCurrentRole] = useState('executive');
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState({
    name: 'Sarah Mitchell',
    email: 'sarah.mitchell@carbonrouter.com',
    avatar: null
  });

  const roles = [
    {
      id: 'executive',
      label: 'Executive View',
      description: 'Fleet-wide oversight and risk management',
      icon: 'Crown',
      permissions: ['dashboard', 'reports', 'trading', 'compliance']
    },
    {
      id: 'chartering',
      label: 'Chartering Desk',
      description: 'Voyage planning and optimization',
      icon: 'Route',
      permissions: ['planning', 'calculator', 'performance', 'compliance']
    },
    {
      id: 'finance',
      label: 'Finance Team',
      description: 'EUA trading and financial analysis',
      icon: 'DollarSign',
      permissions: ['trading', 'reports', 'dashboard']
    },
    {
      id: 'compliance',
      label: 'Compliance Officer',
      description: 'Regulatory oversight and audit trails',
      icon: 'Shield',
      permissions: ['compliance', 'reports', 'dashboard']
    }
  ];

  const getCurrentRole = () => roles?.find(role => role?.id === currentRole);

  const handleRoleSwitch = (roleId) => {
    setCurrentRole(roleId);
    setIsOpen(false);
    
    // Simulate role preference persistence
    localStorage.setItem('userRole', roleId);
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    // Load saved role preference
    const savedRole = localStorage.getItem('userRole');
    if (savedRole && roles?.find(role => role?.id === savedRole)) {
      setCurrentRole(savedRole);
    }
  }, []);

  const currentRoleData = getCurrentRole();

  return (
    <div className="relative">
      {/* User Context Button */}
      <button
        onClick={handleToggle}
        className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-muted transition-maritime"
      >
        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
          {user?.avatar ? (
            <img 
              src={user?.avatar} 
              alt={user?.name}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <Icon name="User" size={16} color="white" strokeWidth={2} />
          )}
        </div>
        
        <div className="hidden lg:block text-left">
          <div className="text-sm font-medium text-foreground">
            {user?.name}
          </div>
          <div className="text-xs text-muted-foreground flex items-center space-x-1">
            <Icon name={currentRoleData?.icon} size={12} strokeWidth={2} />
            <span>{currentRoleData?.label}</span>
          </div>
        </div>
        
        <Icon 
          name="ChevronDown" 
          size={14} 
          strokeWidth={2}
          className={`hidden lg:block transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {/* Context Switcher Panel */}
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-1000" 
            onClick={handleClose}
          />
          <div className="absolute right-0 top-full mt-2 w-80 bg-popover border border-border rounded-lg shadow-maritime-lg z-1100 animate-fade-in">
            <div className="p-4">
              {/* User Info */}
              <div className="flex items-center space-x-3 pb-4 border-b border-border">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="User" size={20} color="white" strokeWidth={2} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    {user?.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {user?.email}
                  </div>
                </div>
              </div>

              {/* Role Switcher */}
              <div className="py-4">
                <h4 className="text-sm font-semibold text-foreground mb-3">
                  Switch Role Context
                </h4>
                
                <div className="space-y-2">
                  {roles?.map((role) => (
                    <button
                      key={role?.id}
                      onClick={() => handleRoleSwitch(role?.id)}
                      className={`w-full flex items-center space-x-3 p-3 rounded-md text-left transition-maritime ${
                        currentRole === role?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted'
                      }`}
                    >
                      <Icon 
                        name={role?.icon} 
                        size={16} 
                        strokeWidth={2}
                        className={currentRole === role?.id ? 'text-primary-foreground' : ''}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          {role?.label}
                        </div>
                        <div className={`text-xs ${
                          currentRole === role?.id 
                            ? 'text-primary-foreground/80' 
                            : 'text-muted-foreground'
                        }`}>
                          {role?.description}
                        </div>
                      </div>
                      {currentRole === role?.id && (
                        <Icon name="Check" size={16} strokeWidth={2} />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="sm" className="text-xs">
                    <Icon name="Settings" size={14} strokeWidth={2} className="mr-2" />
                    Preferences
                  </Button>
                  <Button variant="ghost" size="sm" className="text-xs">
                    <Icon name="LogOut" size={14} strokeWidth={2} className="mr-2" />
                    Sign Out
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserContextSwitcher;